import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Menu,
  MenuProps,
  Stack,
  styled,
  Typography,
  alpha,
  MenuItem,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Chip } from '../../components';
import { RootState } from '../../redux/store';
import { PATHS } from '../../utils/constants';
import Breadcrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import Label from '../../components/Label/Label';
import ActionMenu from './ActionMenu';
import ReleaseVehicleDialog from './ReleaseVehicleDialog';
import { useAppSelector } from '../../hooks/useRedux';
import { getDeliveryProgress } from '../../services/vehicles';
import { customDate } from '../../utils/date';
import { usePermissions } from '../../hooks/usePermissions';

// custom styled menu
const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 4,
    marginTop: theme.spacing(1),
    minWidth: 180,

    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '8px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

interface Props {
  setTransactionErrors?: any;
  retryDecisionPending?: boolean;
}

const InfoHeader: React.FC<Props> = ({ setTransactionErrors, retryDecisionPending }) => {
  // local states
  const { hasPermissions } = usePermissions();
  const [isOpenReleaseVehicleDialogOpen, setIsOpenReleaseVehicleDialogOpen] = useState<boolean>(false);
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [deliveryProgress, setDeliveryProgress] = useState(null);

  // global states
  const vehicle = useSelector((state: RootState) => state.vehicle.data);
  const currentStage = useAppSelector((state) => state.vehicle.selectedStage);

  // console.log('Info Header', vehicle);
  // constants
  const [vin, isVin] = useMemo(() => [vehicle.VIN || vehicle.reservationId, !!vehicle.VIN], [vehicle]);
  const links = useMemo(
    () => [
      { label: 'Dashboard', link: PATHS.dashboard },
      { label: vin, link: PATHS.vahicle(vin) },
    ],
    [vin]
  );
  const open = Boolean(anchorEl);

  // handling open menu
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // handling menu close
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLabelValue = (): { label: string; value: string | undefined } => {
    const obj = { label: '', value: '' };
    if (currentStage === 'Reserved') {
      obj.label = 'Reservation ID';
      obj.value = vehicle?.reservationId;
    } else if (currentStage === 'Ordered') {
      obj.label = 'Ordered ID';
      obj.value = vehicle?.orderId ?? '';
    } else {
      obj.label = 'VIN';
      obj.value = vehicle?.VIN;
    }
    return obj;
  };

  const showActionBtn = () => {
    if (!hasPermissions(['ACCESS_ACTIONS_BUTTON'])) {
      return false;
    }
    if (vehicle.deliveryDoc?.deliveryHandoffDate) {
      return false;
    }
    return true;
  };

  // lifecycle hook
  useEffect(() => {
    getDeliveryProgress(vehicle.stage).then((response) => setDeliveryProgress(response));
  }, [vehicle]);

  return (
    <Box sx={{ backgroundColor: (theme) => theme.palette.common.white, px: 3 }}>
      <ReleaseVehicleDialog
        open={isOpenReleaseVehicleDialogOpen}
        onClose={() => setIsOpenReleaseVehicleDialogOpen(false)}
      />
      <Stack
        direction="row"
        gap={{ xs: 2, sm: 2, md: 3, lg: 5 }}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        marginTop="7px"
      >
        <Stack gap={1}>
          <Breadcrumbs links={links} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', paddingBottom: '10px' }}>
            <Typography variant="h5" fontSize={24} fontWeight={500}>
              {vehicle.First_Name__C} {vehicle.Last_Name__C}
            </Typography>
          </Box>
        </Stack>

        <Box flexGrow={1} />
        <Label label={handleLabelValue()?.label} title={handleLabelValue()?.value} />
        {/* <Label label={isVin ? 'VIN' : 'Reservation ID'} title={vin} /> */}
        <Label label="Phone" title={vehicle.phone} />
        <Label label="Email" title={vehicle.email} titleSx={{ maxWidth: 180 }} />
        <Label label="Assignment Type" title={vehicle.category || '--'} />
        <Label
          label="Delivery scheduled"
          title={vehicle?.deliveryScheduledDateStr ? customDate(vehicle?.deliveryScheduledDateStr) : ''}
        />
        {/* <Label label="Delivery Readiness" type="progress" progress={deliveryProgress || vehicle.deliveryPercentage} /> */}
        <Label
          label="Customer Stage"
          title={vehicle.customerStage}
          status={vehicle?.customerStatus}
          titleSx={{ maxWidth: 250 }}
          type="withStatus"
        />
        {showActionBtn() && (
          <ActionMenu
            vehicle={vehicle}
            isDisable={false}
            setTransactionErrors={setTransactionErrors}
            retryDecisionPending={retryDecisionPending}
          />
        )}

        {/* <Button
          variant="outlined"
          id="demo-customized-button"
          aria-controls={open ? 'demo-customized-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          disableElevation
          onClick={handleClick}
          endIcon={<Icon icon="la:angle-down" width="15" />}
          sx={{
            fontSize: 14,
            fontWeight: 400,
            color: '#456BD9',
            borderRadius: '4px',
            textTransform: 'none',
            padding: '7px 14px',
            lineHeight: '20px',
          }}
        >
          Actions
        </Button> */}
        <StyledMenu
          id="demo-customized-menu"
          MenuListProps={{
            'aria-labelledby': 'demo-customized-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem
            onClick={() => {
              setIsOpenReleaseVehicleDialogOpen(true);
              handleClose();
            }}
            disableRipple
            sx={{ fontSize: '12px', color: '#000', padding: '8px 15px' }}
          >
            Cancel Order
          </MenuItem>
          <MenuItem
            onClick={() => {
              setIsOpenReleaseVehicleDialogOpen(true);
              handleClose();
            }}
            disableRipple
            sx={{ fontSize: '12px', color: '#000', padding: '8px 15px' }}
          >
            Release Vehicle to Inventory
          </MenuItem>
        </StyledMenu>

        {/* <Button
          variant="outlined"
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: '#456BD9',
            borderRadius: '4px',
            textTransform: 'none',
            padding: '4px 10px',
            lineHeight: '20px',
          }}
          onClick={() => setIsOpenReleaseVehicleDialogOpen(true)}
        >
          Release Vehicle
        </Button> */}
      </Stack>
    </Box>
  );
};

export default InfoHeader;
